import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import "./MeetingAttendeesModal.css";

function MeetingAttendeesModal({ meetingId, meeting, onClose }) {
    const [attendees, setAttendees] = useState([]);
    const [members, setMembers] = useState([]);
    const [lodges, setLodges] = useState([]);
    const [formData, setFormData] = useState({
        member: null,
        visitor: false,
        visitorLodge: null,
        visitorName: "",
    });
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState("");
    const [notice, setNotice] = useState("");
    const [listLoading, setListLoading] = useState(true);
    const dialogRef = useRef(null);
    useEffect(() => {
        const previousFocus = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        dialogRef.current?.focus();
        const handleKey = (event) => {
            if (event.key === "Escape") onClose();
            if (event.key === "Tab") {
                const controls = [...dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]')].filter(el => el.getClientRects().length);
                const first = controls[0], last = controls[controls.length - 1];
                if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last?.focus(); }
                else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
            }
        };
        const dialog = dialogRef.current;
        dialog.addEventListener("keydown", handleKey);
        return () => {
            dialog.removeEventListener("keydown", handleKey);
            document.body.style.overflow = previousOverflow;
            previousFocus?.focus();
        };
    }, [onClose]);
    const [submitLoading, setSubmitLoading] = useState(false);

    useEffect(() => {
        fetchAttendees();
        fetchMembers();
        fetchLodges();
    }, [meetingId]);

    const fetchAttendees = async () => {
        setListLoading(true);
        try {
            const res = await axios.get(`/api/meetings/${meetingId}/attendees`);
            setAttendees(res.data);
            setErrors(prev => ({ ...prev, list: null }));
        } catch (error) {
            setErrors(prev => ({ ...prev, list: "Could not load the register. Please try again." }));
        } finally {
            setListLoading(false);
        }
    };

    const fetchMembers = async () => {
        try {
            const res = await axios.get("/api/resources/members");
            // Map to { value, label } format
            const mappedMembers = res.data.map((m) => ({
                value: m.id,
                label: `${m.first_name} ${m.last_name}`,
                firstName: m.first_name,
                lastName: m.last_name,
                mainLodgeId: m.main_lodge_id || m.current_lodge_id || null,
                mainLodgeName: m.main_lodge_name || m.current_lodge_name || "",
                mainLodgeTypeId: m.main_lodge_type_id || null,
                mainLodgeStreamKey: m.main_lodge_stream_key || null,
                mainLodgesByType: Array.isArray(m.main_lodges_by_type) ? m.main_lodges_by_type : [],
                isMainLodge: Boolean(m.main_lodge_id || m.current_membership_is_mother_lodge),
            }));
            setMembers(mappedMembers);
        } catch (error) {
            console.error("Error fetching members:", error);
        }
    };

    const fetchLodges = async () => {
        try {
            const res = await axios.get("/api/resources/lodges");
            // Map to { value, label } format
            const mappedLodges = res.data.map((l) => ({
                value: l.id,
                label: l.name,
                lodgeTypeId: l.lodge_type_id || null,
                lodgeStreamKey: l.lodge_stream_key || null,
            }));
            setLodges(mappedLodges);
        } catch (error) {
            console.error("Error fetching lodges:", error);
        }
    };

    const findVisitorMainLodge = (member) => {
        if (!member) return null;
        const meetingLodgeStreamKey = meeting?.lodge_stream_key || meeting?.lodge_type_id || null;
        const typedMain = meetingLodgeStreamKey
            ? member.mainLodgesByType.find((item) => (item.lodge_stream_key || item.lodge_type_id) === meetingLodgeStreamKey)
            : null;
        const lodgeId = typedMain?.lodge_id || member.mainLodgeId || null;
        return lodgeId ? lodges.find((lodge) => lodge.value === lodgeId) || null : null;
    };

    const handleMemberChange = (selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            member: selectedOption,
            visitorLodge: prev.visitor ? findVisitorMainLodge(selectedOption) : prev.visitorLodge,
            // Reset visitor name if member changes
            visitorName: selectedOption
                ? `${selectedOption.firstName} ${selectedOption.lastName}`
                : "",
        }));
        if (errors.member) {
            setErrors((prevErrors) => ({ ...prevErrors, member: null }));
        }
    };

    const handleVisitorChange = (e) => {
        const checked = e.target.value === "visitor";
        setNotice("");
        setErrors({});
        setFormData((prev) => ({
            ...prev,
            visitor: checked,
            visitorLodge: checked
                ? findVisitorMainLodge(prev.member)
                : null,
            visitorName: checked
                ? prev.member
                    ? `${prev.member.firstName} ${prev.member.lastName}`
                    : ""
                : "",
        }));
        if (errors.visitorLodge) {
            setErrors((prevErrors) => ({ ...prevErrors, visitorLodge: null }));
        }
    };

    useEffect(() => {
        if (!formData.visitor || formData.visitorLodge || !formData.member || !lodges.length) {
            return;
        }

        const mainLodge = findVisitorMainLodge(formData.member);
        if (mainLodge) {
            setFormData((prev) => ({
                ...prev,
                visitorLodge: prev.visitorLodge || mainLodge,
            }));
        }
    }, [formData.visitor, formData.visitorLodge, formData.member, lodges, meeting?.lodge_stream_key, meeting?.lodge_type_id]);

    const handleVisitorLodgeChange = (selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            visitorLodge: selectedOption,
        }));
        if (errors.visitorLodge) {
            setErrors((prevErrors) => ({ ...prevErrors, visitorLodge: null }));
        }
    };

    const validateAttendee = () => {
        const newErrors = {};
        if (!formData.member && !formData.visitor) {
            newErrors.member = "Please select a member.";
        }
        if (formData.visitor && !formData.visitorLodge) {
            newErrors.visitorLodge = "Please select a lodge for the visitor.";
        }
        if (formData.visitor && !formData.visitorName.trim()) {
            newErrors.visitorName = "Please enter the visitor's name.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async (event) => {
        event.preventDefault();
        if (submitLoading) return;
        if (!validateAttendee()) {
            return;
        }

        setNotice("");
        setSubmitLoading(true);
        try {
            const payload = {
                member_id: formData.member ? formData.member.value : null,
                visitor: formData.visitor,
                visitor_lodge_id: formData.visitor ? formData.visitorLodge.value : null,
                visitor_name: formData.visitor ? formData.visitorName.trim() : null,
            };

            await axios.post(`/api/meetings/${meetingId}/attendees`, payload);
            await fetchAttendees();
            await fetchMembers();
            setNotice(`${formData.visitor ? formData.visitorName.trim() : formData.member.label} added to the register.`);
            setFormData(prev => ({ ...prev, member: null, visitorName: "" }));
            setErrors({});
        } catch (error) {
            console.error("Error adding attendee:", error);
            setErrors({ submit: error.response?.data?.error || "Failed to add attendee." });
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to remove this attendee?")) {
            try {
                await axios.delete(`/api/meetings/${meetingId}/attendees/${id}`);
                fetchAttendees();
            } catch (error) {
                console.error("Error removing attendee:", error);
                alert("Failed to remove attendee.");
            }
        }
    };

    const getAttendeeName = (att) => att.visitor_name || members.find(m => m.value === att.member_id)?.label || "Name unavailable";
    const filteredAttendees = attendees.filter(att => `${getAttendeeName(att)} ${lodges.find(l => l.value === att.visitor_lodge_id)?.label || ""}`.toLowerCase().includes(search.toLowerCase()));
    const visitorCount = attendees.filter(att => att.visitor).length;

    return (
        <div className="meeting-attendees-modal-overlay">
            <div className="meeting-attendees-modal-content" role="dialog" aria-modal="true" aria-labelledby="attendance-title" tabIndex={-1} ref={dialogRef}>
                <div className="meeting-attendees-modal-header">
                    <div><p className="attendance-eyebrow">Meeting attendance</p><h2 id="attendance-title">Attendance register</h2>
                    <p className="attendance-context">{meeting?.lodge_name || "Record members and visitors attending this meeting."}</p></div>
                    <button
                        className="meeting-attendees-modal-close-button"
                        onClick={onClose}
                        aria-label="Close Meeting Attendees Modal"
                    >
                        &times;
                    </button>
                </div>
                <div className="meeting-attendees-modal-body">
                    {/* Add Attendee Form */}
                    <form className="meeting-attendees-modal-form" onSubmit={handleSave}>
                        <h3>Add an attendee</h3>
                        <p className="attendance-help">Choose who is attending, then add their details.</p>
                        <fieldset className="attendance-type"><legend>Attendee type</legend>
                            <label className={!formData.visitor ? "selected" : ""}><input type="radio" name="attendeeType" value="member" checked={!formData.visitor} onChange={handleVisitorChange} />Member</label>
                            <label className={formData.visitor ? "selected" : ""}><input type="radio" name="attendeeType" value="visitor" checked={formData.visitor} onChange={handleVisitorChange} />Visitor</label>
                        </fieldset>
                        {/* Member Select */}
                        <div className="meeting-attendees-modal-form-group">
                            <label htmlFor="member-select">{formData.visitor ? "Find an existing member (optional)" : "Member"}</label>
                            <Select
                                inputId="member-select"
                                options={members}
                                value={formData.member}
                                onChange={handleMemberChange}
                                isClearable
                                placeholder="Type to find a member…"
                                classNamePrefix="attendance-select"
                                aria-label="Select Member"
                            />
                            {errors.member && <span className="meeting-attendees-modal-error">{errors.member}</span>}
                        </div>

                        {/* Visitor Details */}
                        {formData.visitor && (
                            <div className="visitor-details">
                                <div className="meeting-attendees-modal-form-group">
                                    <label htmlFor="visitor-lodge-select">Visitor's lodge/body:</label>
                                    <Select
                                        inputId="visitor-lodge-select"
                                        options={lodges}
                                        value={formData.visitorLodge}
                                        onChange={handleVisitorLodgeChange}
                                        placeholder="Type to find a lodge or body…"
                                        classNamePrefix="attendance-select"
                                        aria-label="Select visitor lodge or body"
                                    />
                                    {errors.visitorLodge && (
                                        <span className="meeting-attendees-modal-error">{errors.visitorLodge}</span>
                                    )}
                                </div>
                                <div className="meeting-attendees-modal-form-group">
                                    <label htmlFor="visitor-name-input">Visitor's Name:</label>
                                    <input
                                        type="text"
                                        id="visitor-name-input"
                                        name="visitorName"
                                        autoComplete="name"
                                        aria-invalid={Boolean(errors.visitorName)}
                                        value={formData.visitorName}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                visitorName: e.target.value,
                                            }))
                                        }
                                        placeholder="Enter Visitor's Name..."
                                    />
                                    {errors.visitorName && (
                                        <span className="meeting-attendees-modal-error">{errors.visitorName}</span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Submit Button */}
                        <div className="meeting-attendees-modal-form-group">
                            <button
                                className="meeting-attendees-modal-save-button"
                                type="submit"
                                disabled={submitLoading}
                            >
                                {submitLoading ? "Saving…" : "Add Attendee"}
                            </button>
                            {notice && <p className="attendance-success" role="status">✓ {notice}</p>}
                            {errors.submit && (
                                <span className="meeting-attendees-modal-error" role="alert">{errors.submit}</span>
                            )}
                        </div>
                    </form>
                    <section className="attendance-register-list" aria-label="Registered attendees">
                        <div className="attendance-list-heading"><h3>On the register <span>{attendees.length}</span></h3>
                        <p>{attendees.length - visitorCount} members · {visitorCount} visitors</p></div>
                        <label className="attendance-search-label" htmlFor="attendance-search">Find an attendee</label>
                        <input id="attendance-search" type="search" placeholder="Search name or lodge…" value={search} onChange={e => setSearch(e.target.value)} />
                        {errors.list && <div role="alert" className="meeting-attendees-modal-error">{errors.list} <button type="button" onClick={fetchAttendees}>Retry</button></div>}
                        <ul className="meeting-attendees-modal-attendee-list" aria-busy={listLoading}>
                            {filteredAttendees.map(att => {
                                const name = getAttendeeName(att);
                                const lodge = lodges.find(l => l.value === att.visitor_lodge_id)?.label;
                                return <li key={att.id} className="meeting-attendees-modal-attendee-item">
                                    <div className="attendance-person"><strong>{name}</strong><span>{att.visitor ? `Visitor${lodge ? ` · ${lodge}` : ""}` : "Member"}</span></div>
                                    <button type="button" className="meeting-attendees-modal-attendee-delete-button" onClick={() => handleDelete(att.id)} aria-label={`Remove ${name}`}>Remove</button>
                                </li>;
                            })}
                            {!filteredAttendees.length && <li className="meeting-attendees-modal-no-attendees">{listLoading ? "Loading register…" : attendees.length ? "No matching attendees." : "No one registered yet. Add the first attendee."}</li>}
                        </ul>
                    </section>

                </div>
                <div className="meeting-attendees-modal-footer">
                    <button
                        className="meeting-attendees-modal-close-footer-button"
                        onClick={onClose}
                        aria-label="Close Meeting Attendees Modal"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default MeetingAttendeesModal;
